import { useState, useMemo, useEffect } from 'react';
import { turkishCities } from '../../../mocks/cities';
import { openWhatsApp } from '../../../utils/whatsapp';
import { trackEvent } from '../../../utils/tracking';

const POPULAR_CITIES = ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya'];

interface CityPickerModalProps {
  categoryName: string;
  section: string;
  onClose: () => void;
}

export default function CityPickerModal({ categoryName, section, onClose }: CityPickerModalProps) {
  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const filtered = useMemo(
    () =>
      turkishCities.filter((c) =>
        c.toLocaleLowerCase('tr').includes(citySearch.toLocaleLowerCase('tr'))
      ),
    [citySearch]
  );

  // Close on ESC key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSend = () => {
    if (!selectedCity) return;
    trackEvent('category_click', { category: categoryName, city: selectedCity, section });
    trackEvent('whatsapp_redirect', { category: categoryName, city: selectedCity, section });
    openWhatsApp(categoryName, selectedCity);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #13131a, #1a1a24)',
          border: '1px solid rgba(168,85,247,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-xl"
              style={{ background: 'rgba(168,85,247,0.2)', border: '1px solid rgba(168,85,247,0.35)' }}
            >
              <i className="ri-map-pin-2-fill text-sm text-purple-400" />
            </div>
            <div>
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Şehir Seç
              </p>
              <p className="text-gray-500 text-xs truncate max-w-[200px]">{categoryName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ background: 'rgba(255,255,255,0.06)' }}
            aria-label="Kapat"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
            <input
              autoFocus
              type="text"
              placeholder="Şehir ara... (örn: İstanbul, Ankara)"
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none transition-colors duration-200"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            />
            {citySearch && (
              <button
                onClick={() => setCitySearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Popular cities quick-select */}
        <div className="px-5 pb-3">
          <p className="text-gray-600 text-xs mb-2 flex items-center gap-1.5">
            <i className="ri-fire-line text-orange-400" />
            Popüler Şehirler
          </p>
          <div className="flex flex-wrap gap-2">
            {POPULAR_CITIES.map((city) => (
              <button
                key={city}
                onClick={() => setSelectedCity(city)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 ${
                  selectedCity === city ? 'text-white' : 'text-gray-300 hover:text-white'
                }`}
                style={
                  selectedCity === city
                    ? {
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.35), rgba(236,72,153,0.25))',
                        border: '1px solid rgba(168,85,247,0.6)',
                        boxShadow: '0 0 12px rgba(168,85,247,0.25)',
                      }
                    : {
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                      }
                }
              >
                {selectedCity === city && <i className="ri-check-line text-purple-300" />}
                {city}
              </button>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="mx-5 mb-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }} />

        {/* City grid */}
        <div className="overflow-y-auto px-5 pb-3" style={{ maxHeight: '200px' }}>
          {filtered.length === 0 ? (
            <div className="text-center py-8">
              <i className="ri-map-pin-off-line text-3xl text-gray-700 block mb-2" />
              <p className="text-gray-600 text-sm">Şehir bulunamadı</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {filtered.map((city) => (
                <button
                  key={city}
                  onClick={() => setSelectedCity(city)}
                  className={`relative text-center px-2 py-2.5 rounded-xl text-xs font-medium transition-all duration-150 cursor-pointer whitespace-nowrap truncate ${
                    selectedCity === city ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  style={
                    selectedCity === city
                      ? {
                          background: 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(236,72,153,0.15))',
                          border: '1px solid rgba(168,85,247,0.5)',
                        }
                      : { border: '1px solid transparent' }
                  }
                >
                  {selectedCity === city && (
                    <i className="ri-check-line absolute top-1 right-1 text-purple-400 text-xs" />
                  )}
                  {city}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected city + send button */}
        <div
          className="px-5 py-4 border-t"
          style={{ borderColor: 'rgba(255,255,255,0.07)' }}
        >
          {selectedCity && (
            <div
              className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl text-sm"
              style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.25)' }}
            >
              <i className="ri-map-pin-fill text-purple-400 text-sm" />
              <span className="text-purple-200 text-xs font-medium">Seçilen şehir:</span>
              <span className="text-white text-xs font-bold">{selectedCity}</span>
              <button
                onClick={() => setSelectedCity('')}
                className="ml-auto text-gray-600 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={!selectedCity}
            className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
              selectedCity ? 'text-white hover:scale-[1.02] active:scale-[0.98]' : 'text-gray-600 cursor-not-allowed'
            }`}
            style={
              selectedCity
                ? {
                    background: 'linear-gradient(135deg, #25d366, #128c7e)',
                    boxShadow: '0 6px 24px rgba(37,211,102,0.35)',
                  }
                : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }
            }
          >
            <i className="ri-whatsapp-line text-lg" />
            {selectedCity
              ? `${selectedCity} için Kampanya Al`
              : 'Önce bir şehir seçin'}
            {selectedCity && <i className="ri-arrow-right-line text-sm" />}
          </button>

          <p className="text-center text-gray-700 text-xs mt-2.5">
            KVKK uyumlu · Ücretsiz · İstediğin zaman dur
          </p>
        </div>
      </div>
    </div>
  );
}
